import { useEffect, useContext } from 'preact/hooks';
import { ChatContext } from '../hooks/context.js';
import { showHistory, historySessions, historyError } from '../store/chatStore.js';
import { ICONS } from '../utils/constants.js';

function HistoryEntry({ session: s, onClick, isFloatpanel, delay }) {
  const { cfg } = useContext(ChatContext);
  const timeStr = new Intl.DateTimeFormat(cfg.locale, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(s.started_at));

  useEffect(() => {
    if (!isFloatpanel) return;
    // Stagger fade-in
    const timer = setTimeout(() => {
      const el = document.getElementById(`mc-he-${s.session_id}`);
      if (el) el.classList.add('mc-gh-visible');
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      id={`mc-he-${s.session_id}`}
      class="mc-history-entry"
      role="listitem"
      onClick={() => onClick(s)}
    >
      <div class="mc-history-entry-time">{timeStr}</div>
      <div class="mc-history-entry-preview">{s.preview || '(empty conversation)'}</div>
    </div>
  );
}

export function HistoryPanel({ visible, isFloatpanel, onLoadSession, onClose }) {
  const { cfg, api } = useContext(ChatContext);

  useEffect(() => {
    if (!visible) return;
    historySessions.value = null;
    historyError.value    = null;

    api.fetchSessions()
      .then(data => {
        let sessions = data.sessions || [];
        if (isFloatpanel) {
          sessions = [...sessions].sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
        }
        historySessions.value = sessions;
      })
      .catch(() => { historyError.value = 'Could not load history.'; });
  }, [visible]);

  if (!visible) return null;

  const sessions = historySessions.value;
  const error    = historyError.value;

  const panelClass = isFloatpanel
    ? 'mc-history-panel open'
    : `mc-history-panel${visible ? ' open' : ''}`;

  return (
    <div class={panelClass} aria-label="Recent conversations">
      {!isFloatpanel && (
        <div class="mc-history-head">
          <span>Recent Conversations</span>
          <button class="mc-history-close" aria-label="Close history"
            onClick={onClose} dangerouslySetInnerHTML={{ __html: ICONS.close }} />
        </div>
      )}

      <div class="mc-history-list" role="list">
        {sessions === null && !error && (
          <p class="mc-history-empty">Loading…</p>
        )}
        {error && (
          <p class="mc-history-empty">{error}</p>
        )}
        {sessions !== null && sessions.length === 0 && (
          <p class="mc-history-empty">No previous conversations.</p>
        )}
        {sessions !== null && sessions.map((s, idx) => (
          <HistoryEntry
            key={s.session_id}
            session={s}
            onClick={(rec) => { onLoadSession(rec); onClose(); }}
            isFloatpanel={isFloatpanel}
            delay={isFloatpanel ? (sessions.length - 1 - idx) * 55 : 0}
          />
        ))}
      </div>

      {isFloatpanel && (
        <div class="mc-g-history-back">
          <button class="mc-history-close" aria-label="Back to chat" onClick={onClose}>
            <span dangerouslySetInnerHTML={{ __html: ICONS.back }} /> Back
          </button>
        </div>
      )}
    </div>
  );
}
